import React, { useState } from 'react';
import { Mail, Phone, MapPin, CheckCircle } from 'lucide-react';

const Contact = () => {
  const [formStatus, setFormStatus] = useState('idle'); // idle, submitting, success, error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.target;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://formspree.io/f/xeeeqwvj', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="bg-[#0a0a0a] py-20 px-6">
      <div className="max-w-[1320px] mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[#6a6a6a] text-[13px] mb-3 uppercase tracking-wider">Contact us</p>
          <h2 className="text-[42px] md:text-[52px] font-extralight text-white mb-4 tracking-[-0.02em]">
            Get in Touch
          </h2>
          <p className="text-[#8a8a8a] text-[15px] leading-[1.7] max-w-[500px] mx-auto">
            Ready to explore partnership opportunities? Reach out to discuss how LV1 Group can support your footwear needs.
          </p>
        </div>

        {/* Contact Grid - Liquid Glass */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {/* Email */}
          <a
            href="mailto:info@lv1group.com"
            className="p-8 rounded-[24px] transition-all duration-300 text-center group hover:-translate-y-1"
            style={{
              backdropFilter: 'blur(2px)',
              WebkitBackdropFilter: 'blur(2px)',
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              boxShadow: `inset 0 0 20px rgba(255,255,255,0.05),
                         inset 0 1px 1px rgba(255,255,255,0.1),
                         0px 4px 20px rgba(0,0,0,0.12)`,
              borderTop: '1px solid rgba(255,255,255,0.15)',
              borderLeft: '1px solid rgba(255,255,255,0.1)',
              borderRight: '1px solid rgba(255,255,255,0.05)',
              borderBottom: '1px solid rgba(255,255,255,0.03)',
            }}
          >
            <Mail size={28} className="text-white/60 mx-auto mb-4 group-hover:text-white/80 transition-colors" />
            <h4 className="text-white font-medium text-[15px] mb-2">Email Us</h4>
            <p className="text-[#6a6a6a] text-[14px]">info@lv1group.com</p>
          </a>

          {/* Phone */}
          <a
            href="tel:+1-732-738-6800"
            className="p-8 rounded-[24px] transition-all duration-300 text-center group hover:-translate-y-1"
            style={{
              backdropFilter: 'blur(2px)',
              WebkitBackdropFilter: 'blur(2px)',
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              boxShadow: `inset 0 0 20px rgba(255,255,255,0.05),
                         inset 0 1px 1px rgba(255,255,255,0.1),
                         0px 4px 20px rgba(0,0,0,0.12)`,
              borderTop: '1px solid rgba(255,255,255,0.15)',
              borderLeft: '1px solid rgba(255,255,255,0.1)',
              borderRight: '1px solid rgba(255,255,255,0.05)',
              borderBottom: '1px solid rgba(255,255,255,0.03)',
            }}
          >
            <Phone size={28} className="text-white/60 mx-auto mb-4 group-hover:text-white/80 transition-colors" />
            <h4 className="text-white font-medium text-[15px] mb-2">Call Us</h4>
            <p className="text-[#6a6a6a] text-[14px]">(732) 738-6800</p>
          </a>

          {/* Location */}
          <div
            className="p-8 rounded-[24px] text-center"
            style={{
              backdropFilter: 'blur(2px)',
              WebkitBackdropFilter: 'blur(2px)',
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              boxShadow: `inset 0 0 20px rgba(255,255,255,0.05),
                         inset 0 1px 1px rgba(255,255,255,0.1),
                         0px 4px 20px rgba(0,0,0,0.12)`,
              borderTop: '1px solid rgba(255,255,255,0.15)',
              borderLeft: '1px solid rgba(255,255,255,0.1)',
              borderRight: '1px solid rgba(255,255,255,0.05)',
              borderBottom: '1px solid rgba(255,255,255,0.03)',
            }}
          >
            <MapPin size={28} className="text-white/60 mx-auto mb-4" />
            <h4 className="text-white font-medium text-[15px] mb-2">Visit Us</h4>
            <p className="text-[#6a6a6a] text-[14px]">140 Smith St.<br />Keasbey, NJ 08832</p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-[600px] mx-auto">
          {formStatus === 'success' ? (
            <div
              className="text-center py-12 px-8 rounded-[24px]"
              style={{
                backdropFilter: 'blur(2px)',
                WebkitBackdropFilter: 'blur(2px)',
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                boxShadow: `inset 0 0 20px rgba(255,255,255,0.05),
                           inset 0 1px 1px rgba(255,255,255,0.1),
                           0px 4px 20px rgba(0,0,0,0.12)`,
                borderTop: '1px solid rgba(255,255,255,0.15)',
                borderLeft: '1px solid rgba(255,255,255,0.1)',
                borderRight: '1px solid rgba(255,255,255,0.05)',
                borderBottom: '1px solid rgba(255,255,255,0.03)',
              }}
            >
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={32} className="text-white/80" />
              </div>
              <h3 className="text-white text-[24px] font-light mb-3">Message Sent</h3>
              <p className="text-[#8a8a8a] text-[15px]">
                Thank you for reaching out. We'll get back to you shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  required
                  className="w-full px-5 py-4 bg-[#141414] border border-white/[0.08] rounded-xl text-white placeholder-[#6a6a6a] text-[15px] focus:outline-none focus:border-white/20 transition-colors"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  required
                  className="w-full px-5 py-4 bg-[#141414] border border-white/[0.08] rounded-xl text-white placeholder-[#6a6a6a] text-[15px] focus:outline-none focus:border-white/20 transition-colors"
                />
              </div>
              <input
                type="text"
                name="company"
                placeholder="Company Name"
                className="w-full px-5 py-4 bg-[#141414] border border-white/[0.08] rounded-xl text-white placeholder-[#6a6a6a] text-[15px] focus:outline-none focus:border-white/20 transition-colors"
              />
              <select
                name="inquiry_type"
                required
                className="w-full px-5 py-4 bg-[#141414] border border-white/[0.08] rounded-xl text-[#6a6a6a] text-[15px] focus:outline-none focus:border-white/20 transition-colors appearance-none cursor-pointer"
                defaultValue=""
              >
                <option value="" disabled>Inquiry Type</option>
                <option value="Partnership Opportunity">Partnership Opportunity</option>
                <option value="Brand Licensing">Brand Licensing</option>
                <option value="Private Label">Private Label</option>
                <option value="Distribution">Distribution</option>
                <option value="Other">Other</option>
              </select>
              <textarea
                name="message"
                placeholder="Tell us about your project or inquiry..."
                rows={5}
                required
                className="w-full px-5 py-4 bg-[#141414] border border-white/[0.08] rounded-xl text-white placeholder-[#6a6a6a] text-[15px] focus:outline-none focus:border-white/20 transition-colors resize-none"
              />
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full px-7 py-4 bg-white text-[#0a0a0a] rounded-full text-[15px] font-medium hover:bg-white/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>
              {formStatus === 'error' && (
                <p className="text-red-400 text-[14px] text-center">
                  Something went wrong. Please try again or email us directly.
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;
